import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, Button, Paper } from '@mui/material';
import { OnLoading, SettingsAccessLevel } from '../../types/types';
import { alertError, alertSuccess } from '../../alertDispatcher';
import { SkipEvent } from '../domain/SkipEvent';
import { SkipsProcessingDataProvider } from '../SkipsProcessingDataProvider';
import { SkipEventCard } from './SkipEventCard';
import { SkipsProcessingSidebar } from './SkipsProcessingSidebar';
import { SkipsProcessingJobToProcess } from '../domain/SkipsProcessingJobToProcess';
import { SkipsProcessingMissionManager } from '../domain/SkipsProcessingMissionManager';
import SkipsProcessingDialog from '../../components/mission/maps/SkipsProcessingDialog';
import { getCurrentSession } from '../../dataModelHelpers';
import ExeMissionLoader from '../../services/mission-loading/original/ExeMissionLoader';
import { Session } from '../../db';
import logger from '../../logger';

interface SkipsProcessingTaskProps {
  accessLevel: SettingsAccessLevel;
  onLoading: OnLoading;
  missionActive: string;
}

const SkipsProcessingTask: React.FC<SkipsProcessingTaskProps> = (props) => {
  const { onLoading } = props;

  // State for skip events (main area - Code Ran)
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [skipEvents, setSkipEvents] = useState<SkipEvent[]>([]);

  // State for jobs to process (sidebar)
  const [jobsToProcess, setJobsToProcess] = useState<SkipsProcessingJobToProcess[]>([]);
  const [isSidebarLoading, setIsSidebarLoading] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);

  const dataProvider = useRef(new SkipsProcessingDataProvider());
  const missionManager = useRef(new SkipsProcessingMissionManager());

  // Close current mission if any on mount
  useEffect(() => {
    Session.closeCurrentMission();
  }, []);

  // Load jobs to process from Airtable (sidebar)
  const loadJobsToProcess = useCallback(async () => {
    setIsSidebarLoading(true);

    try {
      const jobRecords = await dataProvider.current.fetchJobsToProcess();
      const jobs = jobRecords.map((record) => new SkipsProcessingJobToProcess(record));

      missionManager.current.setJobs(jobs);
      setJobsToProcess(jobs);

      await logger.log('SKIPS_PROCESSING_JOBS_TO_PROCESS_LOADED', {
        count: jobs.length,
      });
    } catch (error) {
      console.error('Failed to load jobs to process:', error);
      alertError('Failed to load jobs to process from Airtable');
    } finally {
      setIsSidebarLoading(false);
    }
  }, []);

  // Load skip events from Airtable (main area)
  const loadSkipEvents = useCallback(async () => {
    setIsLoading(true);

    try {
      const events = await dataProvider.current.fetchSkipEvents();
      setSkipEvents(events);

      console.log('Loaded skip events:', events);
    } catch (error) {
      console.error('Failed to load skip events:', error);
      alertError('Failed to load skip events from Airtable');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle successful save
  const handleSkipEventSaved = useCallback(() => {
    alertSuccess('Skip event saved successfully');
    loadSkipEvents(); // Reload to get fresh data
  }, [loadSkipEvents]);

  // Handle job click - load mission and open dialog
  const handleLoadJob = useCallback(
    async (jobId: string) => {
      const result = await missionManager.current.activateJob(jobId);

      if (!result.success) {
        if (result.message) {
          alertError(result.message);
        }

        return;
      }

      const job = missionManager.current.getJob(jobId);

      if (!job) {
        missionManager.current.deactivateCurrentJob();

        return;
      }

      await onLoading(async () => {
        const session = getCurrentSession();

        if (!session) {
          missionManager.current.deactivateCurrentJob();
          alertError('No active session');

          return;
        }

        try {
          session.closeCurrentMission(); // Close any current mission

          const missionLoader = new ExeMissionLoader();
          await logger.log(`SkipsProcessingTask: loading job ${jobId}`);

          const loadedMission = await session.loadMission(missionLoader, jobId, false);

          if (!loadedMission) {
            missionManager.current.deactivateCurrentJob();

            return;
          }

          // Open the dialog
          setDialogOpen(true);

          // Update jobs state to trigger re-render
          setJobsToProcess([...missionManager.current.getJobs()]);
        } catch (error) {
          console.error('Failed to load mission:', error);
          alertError(`Failed to load mission for job ${job.name}`);
          missionManager.current.deactivateCurrentJob();
        }
      });
    },
    [onLoading],
  );

  // Handle dialog close - if successful, remove job from sidebar
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);

    // Get the active job ID before deactivating
    const activeJob = missionManager.current.getActiveJob();
    const activeJobId = activeJob?.id;

    // Deactivate the job
    missionManager.current.deactivateCurrentJob();

    // If there was an active job, check if mission was successfully processed
    // The dialog unloads the mission on success, so check if mission is gone
    const session = getCurrentSession();

    if (activeJobId && session && !session.Mission_id) {
      // Mission was successfully processed and unloaded
      // Remove the job from the sidebar list
      setJobsToProcess((prev) => prev.filter((job) => job.id !== activeJobId));

      alertSuccess('Job processed successfully');
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadSkipEvents();
    loadJobsToProcess();
  }, [loadSkipEvents, loadJobsToProcess]);

  return (
    <Box sx={{ display: 'flex', width: '100%', height: 'calc(100vh - 40px)', position: 'relative' }}>
      <SkipsProcessingSidebar
        jobs={jobsToProcess}
        activeJobId={missionManager.current.getActiveJob()?.id || null}
        isLoading={isSidebarLoading}
        onRefresh={loadJobsToProcess}
        onJobClick={handleLoadJob}
      />

      <Box
        sx={{
          flex: 1,
          marginLeft: '350px',
          padding: 3,
          overflow: 'auto',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h2">
            Skips Processing Task
          </Typography>

          <Button variant="outlined" onClick={loadSkipEvents} disabled={isLoading}>
            Refresh Events
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && skipEvents.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No skip events found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Skip events will appear here when jobs are flagged with "Skips - Code Ran"
            </Typography>
          </Paper>
        )}

        {!isLoading && skipEvents.length > 0 && (
          <Box>
            {skipEvents.map((skipEvent) => (
              <SkipEventCard key={skipEvent.getId()} skipEvent={skipEvent} onSaved={handleSkipEventSaved} />
            ))}
          </Box>
        )}
      </Box>

      <SkipsProcessingDialog open={dialogOpen} closeForm={handleDialogClose} />
    </Box>
  );
};

export default SkipsProcessingTask;
